import { getCategory } from "@/lib/api/categories";
import type { Category } from "@/types";

/** Public category / browse listings: allowed `limit` query values (default when absent or invalid: 12). */
export const PUBLIC_CATEGORY_LISTING_PAGE_LIMIT_DEFAULT = 12;
export const PUBLIC_CATEGORY_LISTING_PAGE_LIMIT_MAX = 50;

export function parsePublicCategoryListingLimit(raw: string | null | undefined): number {
	if (raw === `${PUBLIC_CATEGORY_LISTING_PAGE_LIMIT_MAX}`) return PUBLIC_CATEGORY_LISTING_PAGE_LIMIT_MAX;
	return PUBLIC_CATEGORY_LISTING_PAGE_LIMIT_DEFAULT;
}

/** Filter UI uses "Default" as a sentinel; it must not become a literal title/description regex on the API. */
export function normalizePublicListingSearch(raw: string | null | undefined): string | undefined {
	const t = raw?.trim();
	if (!t) return undefined;
	if (t.toLowerCase() === "default") return undefined;
	return t;
}

/** Split a comma-separated query param into non-empty trimmed tokens. */
export function parseCommaList(raw: string | null | undefined): string[] {
	if (raw == null || raw === "") return [];
	return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

/** Resolve a category slug or id string against a merged catalog list. */
export function resolveCategoryRefToId(ref: string, docs: Category[]): string | null {
	const r = ref.trim();
	if (!r) return null;
	const hit = docs.find((c) => String(c._id) === r || String(c.slug ?? "").trim() === r);
	return hit ? String(hit._id) : null;
}

/** Build `category` query param for `/services` (single id or comma-separated mains). */
export function buildServicesCategoryIdParam(
	anchor: Category,
	extraCategoryRefs: string[],
	catalogDocs: Category[],
): string {
	const anchorId = String(anchor._id);
	const extraIds = extraCategoryRefs
		.map((r) => resolveCategoryRefToId(r, catalogDocs))
		.filter((x): x is string => Boolean(x))
		.filter((id) => id !== anchorId);
	const ids = [...new Set([anchorId, ...extraIds])];
	return ids.length === 1 ? ids[0]! : ids.join(",");
}

function stripHtmlSnippet(s: string): string {
	return s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function joinTextsWithLimit(parts: string[], sep: string, max: number): string {
	let out = "";
	for (const raw of parts) {
		const chunk = raw.trim();
		if (!chunk) continue;
		const next = out ? `${out}${sep}${chunk}` : chunk;
		if (next.length <= max) {
			out = next;
		} else {
			const budget = max - (out ? out.length + sep.length : 0);
			if (budget > 12) {
				out =
					(out ? `${out}${sep}` : "") +
					chunk.slice(0, Math.max(0, budget - 1)).trimEnd() +
					"…";
			}
			break;
		}
	}
	return out;
}

export function sortCategoriesByOrder(list: Category[]): Category[] {
	return list.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** `null` = no presence payload (do not infer listing-backed pills). */
function sectorIdListForMainFromPresence(
	sectorIdsByMainId: Record<string, string[]> | undefined,
	mainId: string,
): string[] | null {
	if (!sectorIdsByMainId || Object.keys(sectorIdsByMainId).length === 0) return null;
	const id = String(mainId);
	if (Array.isArray(sectorIdsByMainId[id])) return sectorIdsByMainId[id]!;
	const hit = Object.keys(sectorIdsByMainId).find((k) => String(k) === id);
	return hit != null ? sectorIdsByMainId[hit]! : [];
}

/** Ordered sector titles that have ≥1 published listing in `main` (requires presence map). */
function titlesOfSectorsWithListingsForMain(
	main: Category,
	catalogDocs: Category[],
	sectorIdsByMainId?: Record<string, string[]>,
): string[] {
	const listedIds = sectorIdListForMainFromPresence(sectorIdsByMainId, String(main._id));
	if (listedIds === null) return [];

	const allowed = new Set(listedIds.map(String));
	const applicables = sortCategoriesByOrder(
		catalogDocs.filter((c) => categoryIsIndustryRow(c) && subcategoryAppliesToMain(c, main)),
	);
	return applicables
		.filter(
			(s) => allowed.has(String(s._id)) || (s.id != null && allowed.has(String(s.id))),
		)
		.map((s) => s.title.trim())
		.filter(Boolean);
}

/**
 * Hero copy for `/categories/...` when mains / sectors come from URL + `categories` / `subcategoryIds` query.
 *
 * @param sectorIdsByMainId Optional `getSectorListingPresence` map — when set, single-main banners add
 * listing-backed sector **pills** under the category `subHeading` line.
 */
export function buildCategoryListingBannerCopy(
	catalogDocs: Category[],
	selectedMainIds: string[],
	selectedSubcategoryIds: string[],
	anchorCategory: Category,
	sectorIdsByMainId?: Record<string, string[]>,
): {
	bannerTitle: string;
	/** Comma-separated resolved main category titles (shown in banner blurb when multiple mains are selected). */
	categoryTitlesLine: string;
	bannerSecondaryLine: string | null;
	bannerBlurb: string;
	sectorPillLabels: string[];
} {
	const mainMap = new Map(
		catalogDocs.filter(isTopLevelServiceCategory).map((c) => [String(c._id), c]),
	);
	const mains = sortCategoriesByOrder(
		selectedMainIds.map((id) => mainMap.get(String(id))).filter((c): c is Category => Boolean(c)),
	);
	const categoryTitlesLine =
		mains.length > 0 ? mains.map((m) => m.title).join(", ") : anchorCategory.title;
	const multiMainSelection = mains.length > 1;
	const bannerTitle = multiMainSelection ? "All services" : categoryTitlesLine;

	const subMap = new Map(
		catalogDocs.filter(categoryIsIndustryRow).map((c) => [String(c._id), c]),
	);
	const resolvedSubs =
		selectedSubcategoryIds.length > 0
			? sortCategoriesByOrder(
					selectedSubcategoryIds
						.map((id) => subMap.get(String(id)))
						.filter((c): c is Category => Boolean(c)),
				)
			: [];

	const listingBackedSectorTitles =
		!multiMainSelection && mains.length === 1
			? titlesOfSectorsWithListingsForMain(mains[0]!, catalogDocs, sectorIdsByMainId)
			: [];

	/** All listing-backed discovery pills only when no sector ids are in the URL; otherwise selected sectors only. */
	const sectorPillLabels =
		!multiMainSelection &&
		mains.length === 1 &&
		selectedSubcategoryIds.length === 0 &&
		listingBackedSectorTitles.length > 0
			? listingBackedSectorTitles
			: resolvedSubs.map((s) => s.title.trim()).filter(Boolean);

	const bannerSecondaryLine =
		sectorPillLabels.length > 0 ? sectorPillLabels.join(", ") : null;

	let bannerBlurb = (anchorCategory.subHeading || "").trim();
	if (multiMainSelection) {
		bannerBlurb = categoryTitlesLine;
	} else if (mains.length === 1 && listingBackedSectorTitles.length === 0 && resolvedSubs.length > 0) {
		const snippets = resolvedSubs
			.map((s) => stripHtmlSnippet(s.excerpt || s.description || s.subHeading || ""))
			.filter(Boolean);
		const joined = joinTextsWithLimit(snippets, " ", 260);
		if (joined) bannerBlurb = joined;
	}

	return { bannerTitle, categoryTitlesLine, bannerSecondaryLine, bannerBlurb, sectorPillLabels };
}

/** Marketing “main” bucket: not a filtering subcategory row. */
export function isTopLevelServiceCategory(c: Category): boolean {
	return (
		c.isSubcategory !== true &&
		(c.parentCategoryId == null || String(c.parentCategoryId).trim() === "")
	);
}

/** Industry / subcategory row (explicit flag or legacy: linked to a parent main). */
export function categoryIsIndustryRow(
	c: Pick<Category, "isSubcategory" | "parentCategoryId">,
): boolean {
	if (c.isSubcategory === true) return true;
	const p = c.parentCategoryId;
	return p != null && String(p).trim() !== "";
}

/** Universal subs (no parent) apply to every main; legacy subs only to their parent. */
export function subcategoryAppliesToMain(
	sub: Pick<Category, "isSubcategory" | "parentCategoryId">,
	main: Pick<Category, "_id">,
): boolean {
	if (!categoryIsIndustryRow(sub)) return false;
	const p = sub.parentCategoryId;
	if (p == null || String(p).trim() === "") return true;
	return String(p) === String(main._id);
}

export type PublicCategorySubRoute = { main: Category; sub: Category };

/**
 * Resolve `/categories/:mainSlug/:subSlug` — both segments may be slug or id (caller may redirect to slugs).
 */
export async function resolvePublicCategorySubRoute(
	mainSlugOrId: string,
	subSlugOrId: string,
): Promise<PublicCategorySubRoute | null> {
	let main: Category;
	let sub: Category;
	try {
		main = await getCategory(mainSlugOrId);
	} catch {
		return null;
	}
	try {
		sub = await getCategory(subSlugOrId);
	} catch {
		return null;
	}
	if (!isTopLevelServiceCategory(main)) return null;
	if (!subcategoryAppliesToMain(sub, main)) return null;
	return { main, sub };
}

/** Public listing path: `/categories/{mainSlug}` or `/categories/{mainSlug}/{subSlug}`. */
export function buildPublicCategoryListingPath(
	main: Pick<Category, "slug" | "_id">,
	sub?: Pick<Category, "slug" | "_id"> | null,
): string {
	const a = (main.slug || main._id).trim();
	if (!sub) return `/categories/${a}`;
	const b = (sub.slug || sub._id).trim();
	return `/categories/${a}/${b}`;
}

/** Guard filter-sheet navigation: `/categories`, `/categories/browse`, `/categories/:one`, or `/categories/:one/:two` (slug/id segments). */
export function isSafePublicCategoryListingPath(path: string): boolean {
	const p = path.trim();
	if (p === "/categories") return true;
	if (p === "/categories/browse") return true;
	if (!p.startsWith("/categories/")) return false;
	const rest = p.slice("/categories/".length).split("/").filter(Boolean);
	if (rest.length < 1 || rest.length > 2) return false;
	return rest.every((seg) => /^[a-zA-Z0-9_-]+$/.test(seg));
}

/** First populated sub on a service that can be used in a public nested URL (needs slug on sub doc). */
export function firstServiceSubcategoryWithSlug(service: {
	subcategoryIds?: Array<Category | string> | null;
}): Category | null {
	const subs = service.subcategoryIds;
	if (!Array.isArray(subs) || subs.length === 0) return null;
	for (const raw of subs) {
		if (raw && typeof raw === "object" && "_id" in raw) {
			const c = raw as Category;
			if (!c.slug?.trim()) continue;
			if (c.isSubcategory === false) continue;
			return c;
		}
	}
	return null;
}
